import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UsersIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ArrowLeftIcon,
  BriefcaseIcon,
  IdentificationIcon,
  ArrowDownTrayIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Dropdown from '../components/ui/Dropdown';
import Modal from '../components/ui/Modal';
import EmployeeAvatar from '../components/common/EmployeeAvatar';
import { useAuth } from '../contexts/AuthContext';
import EmployeeService from '../services/employee.service';
import toast from '../utils/toast';
import EmployeeForm from '../components/Employees/EmployeeForm';
import { useHeaderSearch } from '../contexts/SearchContext';
import { formatDateDisplay } from '../utils/dateDisplay';
import { EmployeeTypes } from '../schemas/employee.schema';

const Employees = () => {
  const { user } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedEmploymentStatus, setSelectedEmploymentStatus] = useState('active');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  /** 'add' | 'edit' | null — employee form shown inline (same page), like Leave Request */
  const [employeeFormMode, setEmployeeFormMode] = useState(null);
  const [employeeFormTarget, setEmployeeFormTarget] = useState(null);

  const closeEmployeeForm = () => {
    setEmployeeFormMode(null);
    setEmployeeFormTarget(null);
  };

  const openAddEmployeeForm = () => {
    setSelectedEmployee(null);
    setEmployeeFormTarget(null);
    setEmployeeFormMode('add');
  };

  const openEditEmployeeForm = (emp) => {
    setSelectedEmployee(null);
    setEmployeeFormTarget(emp);
    setEmployeeFormMode('edit');
  };
  const [viewMode, setViewMode] = useState('grid'); // grid or table
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [hardDelete, setHardDelete] = useState(false);
  const [deactivationLeavingDate, setDeactivationLeavingDate] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const employeeFormSectionRef = useRef(null);
  const employeeDetailSectionRef = useRef(null);

  const roles = ['admin', 'hr', 'manager', 'team_lead', 'employee'];

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Combine local search with header search
  const combinedSearch = headerSearch || searchTerm;

  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, headerSearch, selectedDesignation, selectedRole, selectedEmploymentStatus]);

  useEffect(() => {
    if (!employeeFormMode || !employeeFormSectionRef.current) return;
    const id = window.setTimeout(() => {
      employeeFormSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [employeeFormMode]);

  useEffect(() => {
    if (!selectedEmployee || employeeFormMode || !employeeDetailSectionRef.current) return;
    const id = window.setTimeout(() => {
      employeeDetailSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [selectedEmployee, employeeFormMode]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await EmployeeService.getAll();
      setEmployees(EmployeeService.normalizeEmployeeList(response));
    } catch (error) {
      console.error('Error fetching employees:', error);
      const errorMessage = error.message || error.response?.data?.message || 'Failed to load employees';
      toast.error(errorMessage);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    // Inactive records are intentionally hidden from the regular directory.
    // They can only be viewed through the Inactive Employees filter.
    let filtered = employees.filter((emp) => emp.status === selectedEmploymentStatus);

    // Admin accounts stay out of the normal directory, but are available
    // when Admin is explicitly selected in the Role filter.
    if (selectedRole.toLowerCase() === 'admin') {
      filtered = filtered.filter((emp) => emp.role?.toLowerCase() === 'admin');
    } else {
      filtered = filtered.filter((emp) => emp.role?.toLowerCase() !== 'admin');
    }

    if (combinedSearch) {
      filtered = filtered.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(combinedSearch.toLowerCase()) ||
        emp.email_address.toLowerCase().includes(combinedSearch.toLowerCase()) ||
        (emp.empcode && emp.empcode.includes(combinedSearch)) ||
        (emp.designation && emp.designation.toLowerCase().includes(combinedSearch.toLowerCase()))
      );
    }

    if (selectedDesignation) {
      filtered = filtered.filter(emp => emp.designation === selectedDesignation);
    }

    if (selectedRole && selectedRole.toLowerCase() !== 'admin') {
      filtered = filtered.filter(emp => emp.role?.toLowerCase() === selectedRole.toLowerCase());
    }

    setFilteredEmployees(filtered);
  };

  const activeEmployeeCount = employees.filter(
    (emp) => emp.status === 'active' && emp.role?.toLowerCase() !== 'admin'
  ).length;
  const employeeCountForSelectedStatus = employees.filter(
    (emp) =>
      emp.status === selectedEmploymentStatus &&
      emp.role?.toLowerCase() !== 'admin'
  ).length;

  const handleDeleteEmployee = async (employee) => {
    const resolvedEmployee =
      typeof employee === 'string' ? employees.find((e) => e.email_address === employee) : employee;

    if (!resolvedEmployee?.email_address) {
      toast.error('Unable to delete: employee not found');
      return;
    }

    setDeleteTarget(resolvedEmployee);
    const isActive = (resolvedEmployee.is_active ?? resolvedEmployee.isActive ?? resolvedEmployee.status === 'active') === true;
    setHardDelete(!isActive); // default to soft delete for active employees
    setDeactivationLeavingDate(new Date().toISOString().split('T')[0]);
    setDeleteModalOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-xl mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-700 rounded-xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Employee Directory</h1>
              <p className="text-teal-100">Manage employee information and records.</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{activeEmployeeCount}</div>
              <div className="text-sm text-teal-200">Total Employees</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filters and Search */}
      <Card>
        <Card.Body className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
              {/* Search */}
              {/* <div className="relative flex-1 min-w-0 sm:min-w-[260px] sm:max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div> */}

              {/* Designation Filter */}
              <Dropdown
                value={selectedDesignation}
                onChange={setSelectedDesignation}
                options={[
                  { value: '', label: 'All Employees' },
                  ...EmployeeTypes.DESIGNATIONS.map((designation) => ({
                    value: designation,
                    label: designation,
                  })),
                ]}
                placeholder="All Employees"
                className="w-full sm:w-[220px]"
              />

              {/* Employment status filter */}
              <Dropdown
                value={selectedEmploymentStatus}
                onChange={setSelectedEmploymentStatus}
                options={[
                  { value: 'active', label: 'Active Employees' },
                  { value: 'inactive', label: 'Inactive Employees' },
                ]}
                placeholder="Active Employees"
                className="w-full sm:w-[190px]"
              />

              {/* Role Filter */}
              <Dropdown
                value={selectedRole}
                onChange={setSelectedRole}
                options={[
                  { value: '', label: 'All Roles' },
                  ...roles.map(role => ({
                    value: role,
                    label: role.charAt(0).toUpperCase() + role.slice(1)
                  }))
                ]}
                placeholder="All Roles"
                className="w-full sm:w-[180px]"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 lg:justify-end">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                >
                  Table
                </Button>
              </div>

              {/* Add Employee Button */}
              <Button
                onClick={() =>
                  employeeFormMode === 'add' ? closeEmployeeForm() : openAddEmployeeForm()
                }
                icon={PlusIcon}
              >
                Add Employee
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <AnimatePresence>
        {employeeFormMode && (
          <motion.div
            key="employee-form-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div ref={employeeFormSectionRef} className="mb-6 scroll-mt-24">
              <EmployeeForm
                layout="inline"
                employee={employeeFormMode === 'edit' ? employeeFormTarget : null}
                onSuccess={() => {
                  closeEmployeeForm();
                  fetchEmployees();
                }}
                onCancel={closeEmployeeForm}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedEmployee && !employeeFormMode && (
          <motion.div
            key="employee-detail-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div ref={employeeDetailSectionRef} className="mb-6 scroll-mt-24">
              <EmployeeDetailModal
                employee={selectedEmployee}
                layout="inline"
                onClose={() => setSelectedEmployee(null)}
                onEdit={() => {
                  openEditEmployeeForm(selectedEmployee);
                  setSelectedEmployee(null);
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredEmployees.length} of {employeeCountForSelectedStatus}{' '}
          {selectedEmploymentStatus === 'inactive' ? 'inactive' : 'active'} employees
        </p>
        {(combinedSearch || selectedDesignation || selectedRole || selectedEmploymentStatus === 'inactive') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchTerm('');
              setSelectedDesignation('');
              setSelectedRole('');
              setSelectedEmploymentStatus('active');
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Employee Grid/Table */}
      <AnimatePresence mode="wait">
        {viewMode === 'grid' ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredEmployees.map((employee, index) => (
              <motion.div
                key={employee.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <EmployeeCard
                  employee={employee}
                  onEdit={() => openEditEmployeeForm(employee)}
                  onDelete={() => handleDeleteEmployee(employee)}
                  onView={() => {
                    closeEmployeeForm();
                    setSelectedEmployee(employee);
                  }}
                  user={user} 
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="table"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <EmployeeTable
              employees={filteredEmployees}
              onEdit={(emp) => openEditEmployeeForm(emp)}
              onDelete={handleDeleteEmployee}
              onView={(emp) => {
                closeEmployeeForm();
                setSelectedEmployee(emp);
              }}
              user={user}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {filteredEmployees.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
          <p className="text-gray-600 mb-4">
            {combinedSearch || selectedDesignation || selectedRole || selectedEmploymentStatus === 'inactive'
              ? 'Try adjusting your search criteria'
              : 'Get started by adding your first employee'
            }
          </p>
          <Button onClick={openAddEmployeeForm} icon={PlusIcon}>
            Add Employee
          </Button>
        </motion.div>
      )}

      {/* Delete Employee Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteModalOpen(false);
          setDeleteTarget(null);
          setHardDelete(false);
          setDeactivationLeavingDate('');
        }}
        title="Delete employee"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                if (deleteLoading) return;
                setDeleteModalOpen(false);
                setDeleteTarget(null);
                setHardDelete(false);
                setDeactivationLeavingDate('');
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteLoading}
              onClick={async () => {
                if (!deleteTarget?.email_address) return;
                if (!hardDelete && !deactivationLeavingDate) {
                  toast.error('Please select the last working date');
                  return;
                }
                try {
                  setDeleteLoading(true);
                  await EmployeeService.delete(deleteTarget.email_address, {
                    hardDelete,
                    leaving_date: deactivationLeavingDate || null
                  });
                  toast.success(hardDelete ? 'Employee deleted permanently' : 'Employee deactivated (soft delete)');
                  setDeleteModalOpen(false);
                  setDeleteTarget(null);
                  setHardDelete(false);
                  setDeactivationLeavingDate('');
                  fetchEmployees();
                } catch (error) {
                  console.error('Error deleting employee:', error);
                  const errorMessage = error.message || error.response?.data?.message || 'Failed to delete employee';
                  toast.error(errorMessage);
                } finally {
                  setDeleteLoading(false);
                }
              }}
            >
              {hardDelete ? 'Hard delete' : 'Soft delete'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {deleteTarget
              ? `Delete ${deleteTarget.first_name || ''} ${deleteTarget.last_name || ''} (${deleteTarget.email_address})?`
              : 'Delete this employee?'}
          </p>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hardDelete}
              onChange={(e) => setHardDelete(e.target.checked)}
              disabled={deleteLoading}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-900">Hard delete (permanent)</div>
              <div className="text-xs text-gray-600">
                If unchecked, the employee is deactivated (soft delete) and can be restored later.
              </div>
            </div>
          </label>

          {!hardDelete && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last working date (required)
              </label>
              <input
                type="date"
                value={deactivationLeavingDate}
                onChange={(e) => setDeactivationLeavingDate(e.target.value)}
                disabled={deleteLoading}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
              <p className="mt-1 text-xs text-gray-600">
                Attendance reports include this day and exclude later days after departure.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

const EmployeeCard = ({ employee, onEdit, onDelete, onView,user }) => {
  return (
    <Card className="hover:shadow-lg transition-shadow duration-300">
      <Card.Body className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <EmployeeAvatar
              record={employee}
              name={`${employee.first_name || ''} ${employee.last_name || ''}`}
              email={employee.email_address}
              sizeClass="w-12 h-12"
            />
            <div>
              <h3 className="font-semibold text-gray-900">
                {employee.first_name} {employee.last_name}
              </h3>
              <p className="text-sm text-gray-600">#{employee.empcode}</p>
            </div>
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(employee.role)}`}>
            {employee.role}
          </span>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <EnvelopeIcon className="w-4 h-4 mr-2" />
            {employee.email_address}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <BuildingOfficeIcon className="w-4 h-4 mr-2" />
            {employee.designation || 'No designation'}
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Joined {formatDateDisplay(employee.joinDate)}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(employee.status)}`}>
            {employee.status}
          </span>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={onView} icon={EyeIcon} />
            <Button variant="outline" size="sm" onClick={onEdit} icon={PencilIcon} />
            {(user?.role === 'admin' || user?.role === 'HR') && (
              <Button variant="outline" size="sm" onClick={() => onDelete(employee)} icon={TrashIcon} />
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const EmployeeTable = ({ employees, onEdit, onDelete, onView, user }) => {
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Designation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Join Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employees.map((employee) => (
              <tr key={employee.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <EmployeeAvatar
                      record={employee}
                      name={`${employee.first_name || ''} ${employee.last_name || ''}`}
                      email={employee.email_address}
                      sizeClass="w-10 h-10"
                      textClass="text-xs"
                    />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {employee.first_name} {employee.last_name}
                      </div>
                      <div className="text-sm text-gray-500">{employee.email_address}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.designation || 'No designation'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(employee.role)}`}>
                    {employee.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(employee.status)}`}>
                    {employee.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDateDisplay(employee.joinDate)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={() => onView(employee)} icon={EyeIcon} />
                    <Button variant="outline" size="sm" onClick={() => onEdit(employee)} icon={PencilIcon} />
                    {(user?.role === 'admin' || user?.role === 'HR') && (
                      <Button variant="outline" size="sm" onClick={() => onDelete(employee)} icon={TrashIcon} />
                    )}

                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

function escapeVCardValue(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function buildEmployeeVCard(employee) {
  const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
  const org = employee.team?.team_name;
  const phone = employee.phone || employee.phone_no;
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${escapeVCardValue(fullName)}`,
    `N:${escapeVCardValue(employee.last_name || '')};${escapeVCardValue(employee.first_name || '')};;;`,
    org ? `ORG:${escapeVCardValue(org)}` : null,
    employee.designation ? `TITLE:${escapeVCardValue(employee.designation)}` : null,
    `EMAIL;TYPE=INTERNET:${employee.email_address}`,
    phone ? `TEL;TYPE=CELL:${escapeVCardValue(phone)}` : null,
    employee.address
      ? `ADR;TYPE=HOME:;;${escapeVCardValue(employee.address)};;;;`
      : null,
    'END:VCARD'
  ].filter(Boolean);
  return lines.join('\r\n');
}

function downloadEmployeeVCard(employee) {
  const blob = new Blob([buildEmployeeVCard(employee)], {
    type: 'text/vcard;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = `${employee.first_name || 'employee'}_${employee.last_name || ''}`.replace(/\s+/g, '_').slice(0, 80);
  a.href = url;
  a.download = `${safeName || 'contact'}.vcf`;
  a.click();
  URL.revokeObjectURL(url);
}

function formatDetailDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : formatDateDisplay(d);
}

function formatRoleLabel(role) {
  if (!role) return '—';
  const r = String(role);
  if (r === 'team_lead') return 'Team lead';
  return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
}

const EmployeeDetailModal = ({ employee, onClose, onEdit, layout = 'modal' }) => {
  const isActive = employee.status === 'active';
  const teamName = employee.team?.team_name;
  const phoneDisplay = employee.phone || employee.phone_no;
  const joinRaw = employee.joinDate || employee.date_of_joining;
  const leavingRaw = employee.leavingDate || employee.leaving_date;
  const updatedAt = employee.updatedAt || employee.updated_at;
  const isInline = layout === 'inline';
  const containerClass = isInline
    ? 'w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm'
    : 'w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-[0_12px_40px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/80 max-h-[92vh]';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={
        isInline
          ? ''
          : 'fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]'
      }
      onClick={isInline ? undefined : onClose}
      role={isInline ? undefined : 'presentation'}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0, y: 12 }}
        transition={{ type: 'spring', damping: 26, stiffness: 320 }}
        className={containerClass}
        onClick={isInline ? undefined : (e) => e.stopPropagation()}
        role={isInline ? undefined : 'dialog'}
        aria-modal={isInline ? undefined : 'true'}
        aria-labelledby="employee-detail-title"
      >
        <div className="border-b border-slate-100 px-4 pt-4 pb-3 sm:px-6">
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden />
              Directory
            </button>
            <span className="text-slate-300">/</span>
            <span className="font-medium text-slate-800">Employee details</span>
          </div>
        </div>

        <div className="relative bg-gradient-to-b from-slate-50 via-white to-white px-4 pt-8 pb-6 sm:px-8">
          <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
            <div className="relative shrink-0">
              <EmployeeAvatar
                record={employee}
                name={`${employee.first_name || ''} ${employee.last_name || ''}`}
                email={employee.email_address}
                sizeClass="h-24 w-24"
                textClass="text-2xl"
                className="rounded-2xl border-4 border-white shadow-md"
              />
              <div
                className={`absolute bottom-1 right-1 h-4 w-4 rounded-full border-2 border-white ${isActive ? 'bg-emerald-500' : 'bg-red-500'}`}
                title={isActive ? 'Active' : 'Inactive'}
                aria-hidden
              />
            </div>

            <div className="min-w-0 flex-1 text-center md:text-left">
              <div className="flex flex-col items-center gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h1 id="employee-detail-title" className="text-2xl font-bold tracking-tight text-slate-900">
                    {employee.first_name} {employee.last_name}
                  </h1>
                  <p className="mt-1 text-lg font-medium text-slate-600">
                    {employee.designation || 'No designation on file'}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold capitalize ${
                    isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap justify-center gap-3 md:justify-start">
                <a
                  href={`mailto:${employee.email_address}`}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <EnvelopeIcon className="h-5 w-5" aria-hidden />
                  Message
                </a>
                <button
                  type="button"
                  onClick={onEdit}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                >
                  <PencilIcon className="h-5 w-5" aria-hidden />
                  Edit profile
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 bg-white px-4 py-8 sm:px-8 lg:grid-cols-3">
          <div className="space-y-10 lg:col-span-2">
            <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <BriefcaseIcon className="h-6 w-6 text-blue-600" aria-hidden />
                Professional details
              </h2>
              <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Team</span>
                  <span className="text-base font-medium text-slate-900">{teamName || 'Not assigned'}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Designation</span>
                  <span className="text-base font-medium text-slate-900">{employee.designation || '—'}</span>
                </div>
                {employee.address ? (
                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Work address</span>
                    <span className="inline-flex items-start gap-2 text-base font-medium text-slate-900">
                      <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden />
                      {employee.address}
                    </span>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <IdentificationIcon className="h-6 w-6 text-blue-600" aria-hidden />
                General information
              </h2>
              <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Employee ID</span>
                  <span className="font-mono text-base font-medium text-slate-900">
                    {employee.empcode ? `#${employee.empcode}` : '—'}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</span>
                  <span
                    className={`inline-flex w-fit rounded-full px-2.5 py-1 text-sm font-medium capitalize ${getRoleColor(
                      employee.role
                    )}`}
                  >
                    {formatRoleLabel(employee.role)}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</span>
                  <span
                    className={`flex items-center gap-2 text-base font-semibold capitalize ${
                      isActive ? 'text-emerald-700' : 'text-red-600'
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-red-500'}`} aria-hidden />
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Join date</span>
                  <span className="text-base font-medium text-slate-900">{formatDetailDate(joinRaw)}</span>
                </div>
                {!isActive && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Leaving date</span>
                    <span className="text-base font-medium text-slate-900">{formatDetailDate(leavingRaw)}</span>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Work email</span>
                  <span className="break-all text-base font-medium text-slate-900">{employee.email_address}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Record last updated</span>
                  <span className="text-base font-medium text-slate-900">{formatDetailDate(updatedAt)}</span>
                </div>
              </div>
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="h-full rounded-xl border border-blue-100 bg-gradient-to-b from-blue-50/90 to-slate-50/80 p-6 shadow-sm">
              <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <EnvelopeIcon className="h-6 w-6 text-blue-600" aria-hidden />
                Contact information
              </h2>
              <div className="space-y-6">
                <div className="group flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</span>
                  <a
                    href={`mailto:${employee.email_address}`}
                    className="break-all text-base font-semibold text-blue-800 transition hover:text-blue-600"
                  >
                    {employee.email_address}
                  </a>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</span>
                  {phoneDisplay ? (
                    <a
                      href={`tel:${String(phoneDisplay).replace(/\s/g, '')}`}
                      className="text-base font-semibold text-slate-900 transition hover:text-blue-700"
                    >
                      {phoneDisplay}
                    </a>
                  ) : (
                    <span className="text-base text-slate-500">Not provided</span>
                  )}
                </div>
              </div>
              <div className="mt-8 border-t border-blue-200/40 pt-6">
                <button
                  type="button"
                  onClick={() => downloadEmployeeVCard(employee)}
                  className="flex w-full items-center justify-center gap-2 text-sm font-semibold text-blue-700 transition hover:text-blue-900"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" aria-hidden />
                  Download vCard
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-800';
    case 'inactive': return 'bg-red-100 text-red-800';
    case 'on-leave': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getRoleColor = (role) => {
  const r = (role || '').toLowerCase();
  switch (r) {
    case 'admin': return 'bg-purple-100 text-purple-800';
    case 'hr': return 'bg-blue-100 text-blue-800';
    case 'manager': return 'bg-orange-100 text-orange-800';
    case 'team_lead': return 'bg-cyan-100 text-cyan-900';
    case 'employee': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export default Employees;
